import * as d3 from 'd3';
import { createTooltip, ElementWithCleanup, formatValue, hideTooltip, InnerValue, positionTooltip, roundDigitsAfterDecimal, setupTooltipHiding, showTooltip, sidelyChartColors, trimLabel, ValueNameData } from '../cross_platform/dashboard_script';
import { BorderColor2 } from '../../../styles/global/css/Utils';

const BAR_RADIUS = 6;
const LABEL_CHAR_WIDTH = 6.5;
const LABEL_HEIGHT = 16;
const MIN_BAND_WIDTH = 48;
const MIN_ROW_HEIGHT = 22;

export function createBarSvg({
	chartData,
	width,
	height,
	marginTop,
	marginBottom,
	marginLeft,
	marginRight,
	opts,
	tooltipId,
	onValueClick,
}: {
	chartData: ValueNameData[],
	width: number,
	height: number,
	marginTop: number,
	marginBottom: number,
	marginLeft: number,
	marginRight: number,
	opts?: { 
		locale?: string,
		orientation?: 'vertical' | 'horizontal';
		sort?: 'asc' | 'desc' | 'none',
		number_extension?: string,
		max_values?: number,
		digits_after_decimal?: number,
		goals?: boolean,
		trim_x_labels?: number,
		trim_y_labels?: number,
		x_axis_legend?: string,
		y_axis_legend?: string,
		x_axis_boundaries?: {min?: number, max?: number},
		y_axis_boundaries?: {min?: number, max?: number},
	},
	tooltipId?: string,
	onValueClick?: (value: InnerValue) => void,
}): SVGSVGElement | null {
	const orientation = opts?.orientation ?? 'vertical';
	const sortOrder = opts?.sort ?? 'desc';

	 // Compute sorted data once and reuse for both orientations to avoid duplication.
	 // If sortOrder is 'none', preserve original input order.
	let sortedData = sortOrder === 'none' ? [...chartData] : [...chartData].sort((a, b) => (sortOrder === 'asc' ? a.value - b.value : b.value - a.value));
	sortedData = opts?.max_values && chartData.length > Number(opts.max_values) ? sortedData.slice(0, opts.max_values) : sortedData;
	const maxValue = opts?.goals ? 100 : d3.max(sortedData, d => d.value) ?? 0;

	const formatLabel = (d: ValueNameData) => formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), { locale: opts?.locale, number_extension: opts?.number_extension });

	// Grow the drawing area when bands would get too narrow; the container scrolls.
	const widestValueLabel = Math.max(0, ...sortedData.map(d => formatLabel(d).length)) * LABEL_CHAR_WIDTH + 8;
	const drawWidth = orientation === 'vertical' ? Math.max(width, marginLeft + marginRight + sortedData.length * Math.max(MIN_BAND_WIDTH, widestValueLabel)) : width;
	const drawHeight = orientation === 'horizontal' ? Math.max(height, marginTop + marginBottom + sortedData.length * MIN_ROW_HEIGHT) : height;
	const scrolls = drawWidth > width || drawHeight > height;

	// Create the SVG container.
	const svg = d3.create('svg')
		.attr('width', drawWidth)
		.attr('height', drawHeight)
		.attr('viewBox', [0, 0, drawWidth, drawHeight])
		.attr('style', scrolls ? 'flex-shrink: 0;' : 'max-width: 100%; height: auto;');

	const BAR_GAP = 4;

	const roundedTopPath = (x0: number, y0: number, w: number, h: number, r: number) => {
		if (w <= 0 || h <= 0) return '';
		const radius = Math.min(r, w / 2, h / 2);
		return `M ${x0} ${y0 + h} L ${x0} ${y0 + radius} A ${radius} ${radius} 0 0 1 ${x0 + radius} ${y0} L ${x0 + w - radius} ${y0} A ${radius} ${radius} 0 0 1 ${x0 + w} ${y0 + radius} L ${x0 + w} ${y0 + h} Z`;
	};

	const pillPath = (x0: number, y0: number, w: number, h: number, r: number) => {
		if (w <= 0 || h <= 0) return '';
		const radius = Math.min(r, w / 2, h / 2);
		return `M ${x0 + radius} ${y0} L ${x0 + w - radius} ${y0} A ${radius} ${radius} 0 0 1 ${x0 + w} ${y0 + radius} L ${x0 + w} ${y0 + h - radius} A ${radius} ${radius} 0 0 1 ${x0 + w - radius} ${y0 + h} L ${x0 + radius} ${y0 + h} A ${radius} ${radius} 0 0 1 ${x0} ${y0 + h - radius} L ${x0} ${y0 + radius} A ${radius} ${radius} 0 0 1 ${x0 + radius} ${y0} Z`;
	};

	// Create reusable tooltip
	const tooltipDiv = createTooltip({ tooltipId });

	let selectedIndex: number | null = null;

	const baseFill = sidelyChartColors[0];
	const highlightFill = (d3.color(baseFill)?.brighter(0.4) ?? d3.color(baseFill))?.toString() ?? baseFill;

	if (orientation === 'horizontal') {
		// horizontal bars: x = value, y = band (use sortedData order)
		const xMin = opts?.x_axis_boundaries?.min !== undefined ? opts.x_axis_boundaries.min : 0;
		const xMax = opts?.x_axis_boundaries?.max !== undefined ? opts.x_axis_boundaries.max : maxValue;
		const labelSpace = Math.max(...sortedData.map(d => formatLabel(d).length), 0) * LABEL_CHAR_WIDTH + 12;
		const plotRight = width - marginRight - labelSpace;
		const x = d3.scaleLinear()
			.domain([xMin, xMax])
			.range([marginLeft, plotRight]);

		const y = d3.scaleBand<string>()
			.domain(sortedData.map(d => d.name))
			.range([marginTop, drawHeight - marginBottom])
			.padding(0.45);

		// Draw vertical grid lines for x ticks behind the bars.
		const xGrid = d3.axisBottom(x)
			.ticks(6)
			.tickFormat(() => '')
			.tickSize(-(drawHeight - marginTop - marginBottom));
		svg.append('g')
			.attr('transform', `translate(0,${drawHeight - marginBottom})`)
			.call(xGrid)
			.call(g => g.selectAll('line').attr('stroke', BorderColor2).attr('stroke-opacity', 0.9))
			.call(g => g.select('.domain').remove());

		// Add the bars on top of the grid lines. Use paths to allow per-corner rounding and spacing
		const rawBand = y.bandwidth();
		const barH = Math.max(0, rawBand - BAR_GAP);
		const yOffset = (rawBand - barH) / 2;
		svg.append('g')
			.selectAll()
			.data(sortedData)
			.join('path')
			.attr('class', 'bar-rect')
			.attr('d', (d) => {
				const yPos = (y(d.name) as number) + yOffset;
				const w = (x(d.value) as number) - marginLeft;
				return pillPath(marginLeft, yPos, w, barH, BAR_RADIUS);
			})
			.attr('fill', baseFill);

		// Value labels aligned in a column at the right edge.
		svg.append('g')
			.attr('class', 'bar-labels')
			.selectAll()
			.data(sortedData)
			.join('text')
			.attr('class', 'bar-value-label')
			.attr('x', width - marginRight)
			.attr('y', (d) => (y(d.name) as number) + y.bandwidth() / 2)
			.attr('dy', '0.35em')
			.attr('text-anchor', 'end')
			.attr('fill', 'var(--on-surface-color)')
			.style('font-size', '10px')
			.style('font-weight', '500')
			.style('pointer-events', 'none')
			.text(formatLabel);

		// Add an invisible overlay across each band so hovering "in front" of a bar (the whole row)
		// triggers the hover/click behavior, not just the filled portion of the bar.
		svg.append('g')
			.attr('class', 'bar-overlays')
			.selectAll()
			.data(sortedData)
			.join('rect')
			.attr('class', 'bar-overlay')
			.attr('x', () => marginLeft)
			.attr('y', (d) => y(d.name) as number)
			.attr('height', () => y.bandwidth())
			.attr('width', plotRight - marginLeft)
			.attr('fill', 'transparent')
			.style('cursor', onValueClick ? 'pointer' : 'default');

		// Hover line (vertical) for horizontal bars. We'll show/move this on hover/click.
		const hoverLine = svg.append('line')
			.attr('class', `hover-line-${tooltipId ?? 'global'}`)
			.style('display', 'none')
			.attr('stroke', BorderColor2)
			.attr('stroke-opacity', 0.9)
			.attr('stroke-dasharray', '4,3')
			.attr('stroke-width', 1.25);

		// x-axis labels: place numbers under each graduation line without the domain line.
		svg.append('g')
			.attr('transform', `translate(0,${drawHeight - marginBottom})`)
			.call(d3.axisBottom(x).ticks(6).tickFormat((d: d3.NumberValue) => {
				const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
				return String(value);
			}).tickSize(0))
			.call(g => g.select('.domain').remove())
			.call(g => g.selectAll('text')
				.attr('dy', '1.2em')
				.each(function(this: SVGTextElement, d) {
					const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
					d3.select(this).append('title').text(String(value));
				}));

		// y-axis (names)
		svg.append('g')
			.attr('transform', `translate(${marginLeft},0)`)
			.call(d3.axisLeft(y).tickSizeOuter(0).tickFormat(d => trimLabel(String(d), opts?.trim_y_labels)))
			.call(g => g.select('.domain').remove())
			.call(g => g.selectAll('text').each(function(this: SVGTextElement, d) {
				d3.select(this).append('title').text(String(d));
			}));

		// Add x-axis legend (horizontal) if provided
		if (opts?.x_axis_legend) {
			svg.append('text')
				.attr('class', 'x-axis-legend')
				.attr('text-anchor', 'middle')
				.attr('x', marginLeft + (width - marginLeft - marginRight) / 2)
				.attr('y', drawHeight - 10)
				.attr('font-size', '12px')
				.attr('font-weight', '500')
				.attr('fill', 'var(--on-surface-color)')
				.text(opts.x_axis_legend);
		}

		// Add y-axis legend (vertical) if provided
		if (opts?.y_axis_legend) {
			svg.append('text')
				.attr('class', 'y-axis-legend')
				.attr('text-anchor', 'middle')
				.attr('transform', `translate(15, ${marginTop + (drawHeight - marginTop - marginBottom) / 2}) rotate(-90)`)
				.attr('font-size', '12px')
				.attr('font-weight', '500')
				.attr('fill', 'var(--on-surface-color)')
				.text(opts.y_axis_legend);
		}

		// Attach interaction handlers to the transparent overlays so the active area
		// includes the empty space in front of the bar.
		svg.selectAll<SVGRectElement, ValueNameData>('rect.bar-overlay')
			.on('mouseover', (event: MouseEvent, d: ValueNameData) => {
				const bar = svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').filter((d2) => d2.name === d.name && d2.value === d.value);
				bar.transition().duration(120).attr('fill', highlightFill);
				// snap to band center
				const yMid = (y(d.name) as number) + y.bandwidth() / 2;
				const xBarEnd = x(d.value);
				// horizontal dashed line from bar end towards chart right edge
				hoverLine.attr('x1', xBarEnd).attr('x2', plotRight).attr('y1', yMid).attr('y2', yMid).style('display', 'block');
				const content = `<div style="font-weight:600">${d.name}</div><div style="opacity:0.9">${formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), { locale: opts?.locale, number_extension: opts?.number_extension })}</div>`;
				showTooltip(tooltipDiv, content, event);
			})
			.on('mousemove', (event: MouseEvent, d: ValueNameData) => {
				positionTooltip(tooltipDiv, event);
				// keep hover line snapped to band center (do not follow mouse)
				const yMid = (y(d.name) as number) + y.bandwidth() / 2;
				const xBarEnd = x(d.value);
				hoverLine.attr('x1', xBarEnd).attr('x2', plotRight).attr('y1', yMid).attr('y2', yMid);
			})
			.on('mouseout', (event: MouseEvent, d: ValueNameData) => {
				const idx = sortedData.findIndex(s => s.name === d.name && s.value === d.value);
				const bar = svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').filter((d2) => d2.name === d.name && d2.value === d.value);
				if (selectedIndex !== idx) {
					bar.transition().duration(120).attr('fill', baseFill);
					hideTooltip(tooltipDiv);
					hoverLine.style('display', 'none');
				}
			})
			.on('click', (event: MouseEvent, d: ValueNameData) => {
				if (onValueClick) return onValueClick(d.nodeValue);
				const idx = sortedData.findIndex(s => s.name === d.name && s.value === d.value);
				const bar = svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').filter((d2) => d2.name === d.name && d2.value === d.value);
				if (selectedIndex === idx) {
					selectedIndex = null;
					bar.transition().duration(120).attr('fill', baseFill);
					hideTooltip(tooltipDiv);
					hoverLine.style('display', 'none');
				} else {
					// reset others
					svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').transition().duration(120).attr('fill', baseFill);
					selectedIndex = idx;
					bar.transition().duration(120).attr('fill', highlightFill);
					const content = `<div style="font-weight:600">${d.name}</div><div style="opacity:0.9">${formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), { locale: opts?.locale, number_extension: opts?.number_extension })}</div>`;
					showTooltip(tooltipDiv, content, event);
					// ensure hover line pinned and positioned at band center
					const yMid = (y(d.name) as number) + y.bandwidth() / 2;
					const xBarEnd = x(d.value);
					hoverLine.attr('x1', xBarEnd).attr('x2', plotRight).attr('y1', yMid).attr('y2', yMid).style('display', 'block');
				}
			});
	} else {
		// vertical bars (default): x = band, y = value
		const x = d3.scaleBand()
			.domain(sortedData.map(d => d.name))
			.range([marginLeft, drawWidth - marginRight])
			.padding(0.35);

		const yMin = opts?.y_axis_boundaries?.min !== undefined ? opts.y_axis_boundaries.min : 0;
		const yMax = opts?.y_axis_boundaries?.max !== undefined ? opts.y_axis_boundaries.max : maxValue;
		const y = d3.scaleLinear()
			.domain([yMin, yMax])
			.range([height - marginBottom, marginTop + LABEL_HEIGHT]);

		// Draw horizontal grid lines for y ticks behind the bars.
		const yGrid = d3.axisLeft(y)
			.ticks(6)
			.tickFormat(() => '')
			.tickSize(-(drawWidth - marginLeft - marginRight));
		svg.append('g')
			.attr('transform', `translate(${marginLeft},0)`)
			.call(yGrid)
			.call(g => g.selectAll('line').attr('stroke', BorderColor2).attr('stroke-opacity', 0.9))
			.call(g => g.select('.domain').remove());

		// Add a path for each bar on top of the grid lines. Use spacing and rounded top corners.
		const rawBandV = x.bandwidth();
		const barWv = Math.max(0, rawBandV - BAR_GAP);
		const xOffsetV = (rawBandV - barWv) / 2;
		svg.append('g')
			.selectAll()
			.data(sortedData)
			.join('path')
			.attr('class', 'bar-rect')
			.attr('d', (d) => {
				const xPos = (x(d.name) as number) + xOffsetV;
				const yTop = y(d.value) as number;
				const h = (y(0) as number) - yTop;
				return roundedTopPath(xPos, yTop, barWv, h, BAR_RADIUS);
			})
			.attr('fill', baseFill);

		// Value labels above each vertical bar.
		svg.append('g')
			.attr('class', 'bar-labels')
			.selectAll()
			.data(sortedData)
			.join('text')
			.attr('class', 'bar-value-label')
			.attr('x', (d) => (x(d.name) as number) + x.bandwidth() / 2)
			.attr('y', (d) => (y(d.value) as number) - 6)
			.attr('text-anchor', 'middle')
			.attr('fill', 'var(--on-surface-color)')
			.style('font-size', '10px')
			.style('font-weight', '500')
			.style('pointer-events', 'none')
			.text(formatLabel)
			.style('display', d => formatLabel(d).length * LABEL_CHAR_WIDTH + 4 > x.step() ? 'none' : null);

		// Add invisible overlays for vertical bars so hovering anywhere in the column triggers interaction.
		svg.append('g')
			.attr('class', 'bar-overlays')
			.selectAll()
			.data(sortedData)
			.join('rect')
			.attr('class', 'bar-overlay')
			.attr('x', (d) => x(d.name) as number)
			.attr('y', () => marginTop)
			.attr('width', x.bandwidth())
			.attr('height', height - marginTop - marginBottom)
			.attr('fill', 'transparent')
			.style('cursor', onValueClick ? 'pointer' : 'default');

		// Hover line (horizontal) for vertical bars.
		const hoverLine = svg.append('line')
			.attr('class', `hover-line-${tooltipId ?? 'global'}`)
			.style('display', 'none')
			.attr('stroke', BorderColor2)
			.attr('stroke-opacity', 0.9)
			.attr('stroke-dasharray', '4,3')
			.attr('stroke-width', 1.25);

		// x-axis: remove domain line so only labels remain near each grid line. Skip ticks whose labels would overlap.
		const tickLabelWidth = Math.max(0, ...sortedData.map(d => trimLabel(String(d.name), opts?.trim_x_labels).length)) * LABEL_CHAR_WIDTH + 8;
		const tickStep = Math.max(1, Math.ceil(tickLabelWidth / Math.max(1, x.bandwidth())));
		svg.append('g')
			.attr('transform', `translate(0,${height - marginBottom})`)
			.call(d3.axisBottom(x).tickSize(0).tickValues(sortedData.map(d => d.name).filter((_, i) => i % tickStep === 0)).tickFormat(d => trimLabel(String(d), opts?.trim_x_labels)))
			.call(g => g.select('.domain').remove())
			.call(g => g.selectAll('text').each(function(this: SVGTextElement, d) {
				d3.select(this).append('title').text(String(d));
			}));

		// Add the y-axis and label, and remove the domain line.
		svg.append('g')
			.attr('transform', `translate(${marginLeft},0)`)
			.call(d3.axisLeft(y).tickFormat((v: d3.NumberValue, _i: number) => {
				const value = roundDigitsAfterDecimal(v as number, opts?.digits_after_decimal);
				return opts?.trim_y_labels ? trimLabel(String(value), opts.trim_y_labels) : String(value);
			}))
			.call(g => g.select('.domain').remove())
			.call(g => g.selectAll('text').each(function(this: SVGTextElement, v) {
				const value = roundDigitsAfterDecimal(v as d3.NumberValue as number, opts?.digits_after_decimal);
				d3.select(this).append('title').text(String(value));
			}));

		// Add x-axis legend (horizontal) if provided
		if (opts?.x_axis_legend) {
			svg.append('text')
				.attr('class', 'x-axis-legend')
				.attr('text-anchor', 'middle')
				.attr('x', marginLeft + (drawWidth - marginLeft - marginRight) / 2)
				.attr('y', height - 10)
				.attr('font-size', '12px')
				.attr('font-weight', '500')
				.attr('fill', 'var(--on-surface-color)')
				.text(opts.x_axis_legend);
		}

		// Add y-axis legend (vertical) if provided
		if (opts?.y_axis_legend) {
			svg.append('text')
				.attr('class', 'y-axis-legend')
				.attr('text-anchor', 'middle')
				.attr('transform', `translate(15, ${marginTop + (height - marginTop - marginBottom) / 2}) rotate(-90)`)
				.attr('font-size', '12px')
				.attr('font-weight', '500')
				.attr('fill', 'var(--on-surface-color)')
				.text(opts.y_axis_legend);
		}

		// Attach interaction handlers to the transparent overlays so hovering anywhere in the column
		// triggers the hover/click behavior. Draw a vertical dashed line snapped to band center and
		// extending from the band center up to the top margin of the chart.
		svg.selectAll<SVGRectElement, ValueNameData>('rect.bar-overlay')
			.on('mouseover', (event: MouseEvent, d: ValueNameData) => {
				const bar = svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').filter((d2) => d2.name === d.name && d2.value === d.value);
				bar.transition().duration(120).attr('fill', highlightFill);
				// snap to band center X
				const xMid = (x(d.name) as number) + x.bandwidth() / 2;
				const yBarTop = y(d.value) as number;
				// vertical dashed line from chart top (marginTop) down to bar top
				hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', marginTop).attr('y2', yBarTop).style('display', 'block');
				const content = `<div style="font-weight:600">${d.name}</div><div style="opacity:0.9">${formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), { locale: opts?.locale, number_extension: opts?.number_extension })}</div>`;
				showTooltip(tooltipDiv, content, event);
			})
			.on('mousemove', (event: MouseEvent, d: ValueNameData) => {
				positionTooltip(tooltipDiv, event);
				// keep hover line snapped to band center X (do not follow mouse)
				const xMid = (x(d.name) as number) + x.bandwidth() / 2;
				const yBarTop = y(d.value) as number;
				hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', marginTop).attr('y2', yBarTop);
			})
			.on('mouseout', (event: MouseEvent, d: ValueNameData) => {
				const idx = sortedData.findIndex(s => s.name === d.name && s.value === d.value);
				const bar = svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').filter((d2) => d2.name === d.name && d2.value === d.value);
				if (selectedIndex !== idx) {
					bar.transition().duration(120).attr('fill', baseFill);
					hideTooltip(tooltipDiv);
					hoverLine.style('display', 'none');
				}
			})
			.on('click', (event: MouseEvent, d: ValueNameData) => {
				if (onValueClick) return onValueClick(d.nodeValue);
				const idx = sortedData.findIndex(s => s.name === d.name && s.value === d.value);
				const bar = svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').filter((d2) => d2.name === d.name && d2.value === d.value);
				if (selectedIndex === idx) {
					selectedIndex = null;
					bar.transition().duration(120).attr('fill', baseFill);
					hideTooltip(tooltipDiv);
					hoverLine.style('display', 'none');
				} else {
					// reset others
					svg.selectAll<SVGPathElement, ValueNameData>('.bar-rect').transition().duration(120).attr('fill', baseFill);
					selectedIndex = idx;
					bar.transition().duration(120).attr('fill', highlightFill);
					const content = `<div style="font-weight:600">${d.name}</div><div style="opacity:0.9">${formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), { locale: opts?.locale, number_extension: opts?.number_extension })}</div>`;
					showTooltip(tooltipDiv, content, event);
					// ensure hover line pinned and positioned at band center
					const xMid = (x(d.name) as number) + x.bandwidth() / 2;
					const yBarTop = y(d.value) as number;
					hoverLine.attr('x1', xMid).attr('x2', xMid).attr('y1', marginTop).attr('y2', yBarTop).style('display', 'block');
				}
			});
	}

	// Setup global tooltip hiding with cleanup
	const cleanup = setupTooltipHiding(tooltipDiv, () => {
		// hide hover line if present
		svg.selectAll<SVGLineElement, unknown>(`line.${'hover-line-' + (tooltipId ?? 'global')}`).style('display', 'none');
		selectedIndex = null;
		// restore bar fills
		svg.selectAll<SVGPathElement, unknown>('.bar-rect').transition().duration(120).attr('fill', baseFill);
	});

	const svgNode = svg.node();
	if (!svgNode) return null;
	
	// Store cleanup function on the SVG element
	(svgNode as ElementWithCleanup)._cleanupTooltip = cleanup;
	
	return svgNode;
}
